import {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useRouter } from "next/router";
import { useRecoilState, useSetRecoilState } from "recoil";
import { v4 as uuidv4 } from "uuid";

import { getAndSetProductById } from "@/services/hooks";
import { returnResult } from "@/utils/helpers";
import { useGomakeAxios, useGomakeRouter, useSnackBar } from "@/hooks";
import { compareStrings } from "@/utils/constants";
import {
  GraphicIcon,
  HiddenIcon,
  NotHiddenIcon,
  ParameterIcon,
  SettingIcon,
} from "@/widgets";
import {
  GoMakeAutoComplate,
  GomakePrimaryButton,
  GomakeTextInput,
  SecondSwitch,
} from "@/components";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { AIIcon } from "@/icons/side-list-icons/ai";

import { useMaterials } from "../use-materials";
import { EButtonTypes, EParameterTypes } from "@/enums";
import cloneDeep from "lodash.clonedeep";
import {
  doNotAskNextTimeState,
  selectedUpdateTypeState,
  selectParameterButtonState,
} from "@/store";
import { ButtonParameterWidget } from "@/pages-components/products/digital-offset-price/widgets/render-parameter-widgets/button-parameter";
import { findParameterByCode } from "@/utils/helpers";
import {
  findParameterByIdInTemplate,
  mergeParameterValuesConfigsInTemplate,
} from "@/utils/product-template-merge";
import { SettingsIcon } from "@/icons/settings";
import { UpdateTypes } from "@/widgets/shared-admin-customers/add-product/parameters/widgets/section-mapping";
import {
  ESourceView,
  EUpdateActionType,
  ProductLogActionEnum,
} from "@/enums/products";

const PRODUCT_REFETCH_DEBOUNCE_MS = 350;

function resolveMaterialOptionValueId(option: any): unknown {
  if (option == null) {
    return undefined;
  }
  if (option.valueId != null && option.valueId !== "") {
    return option.valueId;
  }
  return undefined;
}

/** مطابقة صف valuesConfig مع valueId (أي عنصر داخل materialValueIds، ليس [0] فقط). */
function materialValueConfigMatchesValueId(
  config: any,
  valueId: unknown
): boolean {
  if (valueId == null || valueId === "") {
    return false;
  }
  const ids = config?.materialValueIds;
  if (!Array.isArray(ids)) {
    return false;
  }
  const s = String(valueId);
  return ids.some(
    (mv: any) =>
      mv != null &&
      mv.valueId != null &&
      mv.valueId !== "" &&
      (mv.valueId === valueId || compareStrings(String(mv.valueId), s))
  );
}

/**
 * يكمل `valuesConfigs` بصف مؤقت لكل بند في قائمة المواد المعروضة وغير الموجود في القالب
 * (حتى تنجح المطابقة والتبديل دون الاعتماد فقط على ما حُفظ مسبقاً).
 */
function mergeVisibleMaterialListIntoValuesConfigs(
  valuesConfigs: any[] | null | undefined,
  listOptions: any[] | null | undefined,
  isVariationProductMode: boolean
): any[] {
  const base = cloneDeep(valuesConfigs || []);
  const list = Array.isArray(listOptions) ? listOptions : [];
  for (const opt of list) {
    const vid = resolveMaterialOptionValueId(opt);
    if (vid == null || vid === "") {
      continue;
    }
    if (base.some((row) => materialValueConfigMatchesValueId(row, vid))) {
      continue;
    }
    base.push({
      id: uuidv4(),
      isHidden: isVariationProductMode,
      isDefault: false,
      isDeleted: false,
      value: opt?.value,
      materialValueIds: [
        {
          path: opt?.pathName,
          valueId: vid,
          value: opt?.value,
        },
      ],
    });
  }
  return base;
}

/** Prefer the parameter currently rendered; fall back to the template tree by id. */
function cloneValuesConfigsForParameter(
  templateRoot: any | null | undefined,
  parameter: any
): any[] {
  if (
    Array.isArray(parameter?.valuesConfigs) &&
    parameter.valuesConfigs.length > 0
  ) {
    return cloneDeep(parameter.valuesConfigs);
  }
  if (templateRoot && parameter?.id) {
    const fromTemplate = findParameterByIdInTemplate(templateRoot, parameter.id);
    if (Array.isArray(fromTemplate?.valuesConfigs)) {
      return cloneDeep(fromTemplate.valuesConfigs);
    }
  }
  return [];
}

function collectParametersFromTemplate(node: any, acc: any[] = []): any[] {
  if (!node) {
    return acc;
  }
  if (Array.isArray(node.parameters)) {
    for (const p of node.parameters) {
      if (!p) {
        continue;
      }
      acc.push(p);
      if (Array.isArray(p.childsParameters) && p.childsParameters.length > 0) {
        collectParametersFromTemplate({ parameters: p.childsParameters }, acc);
      }
    }
  }
  if (Array.isArray(node.sections)) {
    for (const s of node.sections) {
      collectParametersFromTemplate(s, acc);
    }
  }
  if (Array.isArray(node.subSections)) {
    for (const ss of node.subSections) {
      collectParametersFromTemplate(ss, acc);
    }
  }
  return acc;
}

/** Keep `isHidden` from the current UI when a refetch incorrectly clears visibility flags. */
function preserveValuesConfigsHiddenOnRefetch(
  previousRoot: any,
  nextRoot: any
): any {
  if (!previousRoot || !nextRoot) {
    return nextRoot;
  }
  const result = cloneDeep(nextRoot);
  const prevByParamId = new Map(
    collectParametersFromTemplate(previousRoot).map((p) => [p.id, p])
  );

  for (const nextParam of collectParametersFromTemplate(result)) {
    const prevParam = prevByParamId.get(nextParam.id);
    if (!prevParam?.valuesConfigs || !nextParam?.valuesConfigs) {
      continue;
    }
    const prevConfigById = new Map<string, any>(
      prevParam.valuesConfigs.map((vc: any) => [vc.id, vc])
    );
    nextParam.valuesConfigs = nextParam.valuesConfigs.map((vc: any) => {
      const prevVc = prevConfigById.get(vc.id) as any;
      if (prevVc?.isHidden === true && vc?.isHidden !== true) {
        return { ...vc, isHidden: true };
      }
      return vc;
    });
  }
  return result;
}

const useAddProduct = ({ classes }) => {
  const { callApi } = useGomakeAxios();
  const { navigate } = useGomakeRouter();
  const { alertFaultAdded, alertSuccessAdded, alertFault } = useSnackBar();
  const [expanded, setExpanded] = useState<string | false>("");
  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const isVariationProductMode = useMemo(() => {
    const queryValue = router?.query?.isVariationProduct;
    if (Array.isArray(queryValue)) {
      return queryValue[0]?.toLowerCase() === "true";
    }
    return String(queryValue).toLowerCase() === "true";
  }, [router?.query?.isVariationProduct]);
  const directionForUi = t("direction") as "rtl" | "ltr";
  const visibilityParameterAutocompleteComponentsProps = useMemo(
    () => ({
      popper: {
        placement:
          directionForUi === "rtl"
            ? ("bottom-end" as const)
            : ("bottom-start" as const),
        modifiers: [
          {
            name: "flip",
            enabled: false,
          },
          {
            name: "preventOverflow",
            enabled: true,
            options: {
              altAxis: true,
              padding: 8,
            },
          },
        ],
      },
    }),
    [directionForUi],
  );
  const [productState, setProductState] = useState<any>([]);
  const [changeName, setChangeName] = useState("");
  const [changeDefaultValue, setChangeDefaultValue] = useState("");
  const { allMaterials, getAllMaterial } = useMaterials();
  const [GalleryModalOpen, setGalleryModalOpen] = useState(false);
  const setSelectParameterButton = useSetRecoilState(
    selectParameterButtonState
  );
  const straightKnife = findParameterByCode(productState, "IsStraightKnife");
  const [multiParameterModal, setMultiParameterModal] = useState(false);
  const [colorOptionsModalOpen, setColorOptionsModalOpen] = useState(false);
  const [rollDirectionOptionsModalOpen, setRollDirectionOptionsModalOpen] =
    useState(false);
  const [settingParameters, setSettingParameters] = useState({});
  const [isCalculatingVariations, setIsCalculatingVariations] = useState(false);
  const [isConfirmingVariations, setIsConfirmingVariations] = useState(false);
  const [isVariationsSummaryModalOpen, setIsVariationsSummaryModalOpen] =
    useState(false);
  const [productVariationsSummary, setProductVariationsSummary] = useState("");

  const onCloseMultiParameterModal = () => {
    setMultiParameterModal(false);
  };

  const onCloseColorOptionsModal = () => {
    setColorOptionsModalOpen(false);
  };

  const onOpenColorOptionsModal = (
    parameter: any,
    sectionId: string,
    subSectionId: string
  ) => {
    setSelectedParameter(parameter);
    setSelectedSectonId(sectionId);
    setSelectedSubSection(subSectionId);
    setTimeout(() => {
      setColorOptionsModalOpen(true);
    }, 100);
  };

  const onCloseRollDirectionOptionsModal = () => {
    setRollDirectionOptionsModalOpen(false);
  };

  const onOpenRollDirectionOptionsModal = (
    parameter: any,
    sectionId: string,
    subSectionId: string
  ) => {
    setSelectedParameter(parameter);
    setSelectedSectonId(sectionId);
    setSelectedSubSection(subSectionId);
    setTimeout(() => {
      setRollDirectionOptionsModalOpen(true);
    }, 100);
  };

  const onOpeneMultiParameterModal = (
    paameters,
    subSection,
    section,
    subSectionParameters
  ) => {
    setMultiParameterModal(true);
    const value = paameters?.valuesConfigs?.find((value) => value?.isDefault);
    setSettingParameters({
      parameter: paameters,
      subSection: subSection,
      section: section,
      subSectionParameters: subSectionParameters,
      value: value,
    });
  };
  const onCloseGalleryModal = () => {
    setGalleryModalOpen(false);
  };
  const onOpeneGalleryModal = () => {
    setGalleryModalOpen(true);
  };
  const onChangeStateProduct = useCallback(
    (filedName: string, value: any) => {
      setProductState((prev) => {
        return {
          ...prev,
          [filedName]: value,
        };
      });
    },
    [productState]
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [template, setTemplate] = useState<any>();
  const templateRef = useRef<any>(null);
  const visibilityPaperContextRef = useRef<string | null>(null);
  const valueConfigActionRef = useRef<"hidden" | "ai">("hidden");
  const visibilityDraftRef = useRef<{
    parameterId: string;
    valuesConfigs: any[];
  } | null>(null);
  const visibilityDraftDirtyRef = useRef(false);
  useEffect(() => {
    templateRef.current = template;
  }, [template]);

  const fetchGenerationRef = useRef(0);
  const debounceRefetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const parameterUpdateChainRef = useRef<Record<string, Promise<unknown>>>({});

  useEffect(() => {
    return () => {
      if (debounceRefetchTimerRef.current) {
        clearTimeout(debounceRefetchTimerRef.current);
      }
    };
  }, []);

  const runSerializedParameterUpdate = useCallback(
    (parameterId: string, fn: () => Promise<void>) => {
      const key = parameterId || "__global_parameter__";
      const prev = parameterUpdateChainRef.current[key] ?? Promise.resolve();
      const next = prev.then(fn).catch(() => { });
      parameterUpdateChainRef.current[key] = next.finally(() => {
        if (parameterUpdateChainRef.current[key] === next) {
          delete parameterUpdateChainRef.current[key];
        }
      });
      return next;
    },
    []
  );

  const handleTabClick = (index: number) => {
    if (index !== activeIndex) {
      setActiveIndex(index);
    }
  };
  const handleNextClick = () => {
    if (activeIndex < template.sections.length - 1) {
      setActiveIndex(activeIndex + 1);
    }
  };
  const buildCalculateProductVariationsPayload = useCallback(() => {
    const productId = Array.isArray(router?.query?.id)
      ? router?.query?.id[0]
      : router?.query?.id;

    if (!productId || typeof productId !== "string") {
      return null;
    }

    const rawVariationName = router?.query?.ProdutVatiationName;
    const produtVatiationName = Array.isArray(rawVariationName)
      ? rawVariationName[0]
      : rawVariationName;

    const payload: { productId: string; productName?: string } = {
      productId,
    };

    if (
      typeof produtVatiationName === "string" &&
      produtVatiationName.trim()
    ) {
      payload.productName = produtVatiationName.trim();
    }

    return payload;
  }, [router?.query?.id, router?.query?.ProdutVatiationName]);

  const extractProductVariationsSummary = useCallback((response: any) => {
    const nested = response?.data?.data;
    if (typeof nested === "string") {
      return nested;
    }
    if (typeof nested?.data === "string") {
      return nested.data;
    }
    if (typeof nested?.result === "string") {
      return nested.result;
    }
    return "";
  }, []);

  const handleCloseVariationsSummaryModal = useCallback(() => {
    setIsVariationsSummaryModalOpen(false);
    setProductVariationsSummary("");
  }, []);

  const handleCalculateProductVariations = useCallback(async () => {
    const payload = buildCalculateProductVariationsPayload();

    if (!payload) {
      alertFault("products.offsetPrice.admin.errorReq");
      return;
    }

    try {
      setIsCalculatingVariations(true);
      const response = await callApi(
        "POST",
        "/v1/erp-service/product-item-value/get-product-variations-summary",
        payload
      );

      if (!response?.success) {
        alertFaultAdded();
        return;
      }

      const summary = extractProductVariationsSummary(response);
      if (!summary?.trim()) {
        alertFaultAdded();
        return;
      }

      setProductVariationsSummary(summary);
      setIsVariationsSummaryModalOpen(true);
    } catch {
      alertFaultAdded();
    } finally {
      setIsCalculatingVariations(false);
    }
  }, [
    buildCalculateProductVariationsPayload,
    callApi,
    alertFault,
    alertFaultAdded,
    extractProductVariationsSummary,
  ]);

  const handleConfirmCalculateProductVariations = useCallback(async () => {
    const payload = buildCalculateProductVariationsPayload();

    if (!payload) {
      alertFault("products.offsetPrice.admin.errorReq");
      return;
    }

    try {
      setIsConfirmingVariations(true);
      const response = await callApi(
        "POST",
        "/v1/erp-service/product-item-value/calculate-product-variations",
        payload
      );

      if (response?.success) {
        handleCloseVariationsSummaryModal();
        alertSuccessAdded();
        navigate("/settings/products-variations");
      } else {
        alertFaultAdded();
      }
    } catch {
      alertFaultAdded();
    } finally {
      setIsConfirmingVariations(false);
    }
  }, [
    buildCalculateProductVariationsPayload,
    callApi,
    alertFault,
    alertSuccessAdded,
    alertFaultAdded,
    navigate,
    handleCloseVariationsSummaryModal,
  ]);
  const handlePreviousClick = () => {
    if (activeIndex != 0) {
      setActiveIndex(activeIndex - 1);
    }
  };
  const [activeTab, setActiveTab] = useState("Settings");
  const onClickSettingsTab = () => {
    setActiveTab("Settings");
  };
  const onClickParametersTab = () => {
    setActiveTab("Parameters");
  };
  const onClickGraphicTab = () => {
    setActiveTab("Graphic");
  };

  const tabs = [
    {
      name: "Settings",
      icon: <SettingIcon stroke={"var(--theme-primary-700, #1C1D58)"} />,
      activeIcon: <SettingIcon />,
      onclick: () => onClickSettingsTab,
    },
    {
      name: "Parameters",
      icon: <ParameterIcon />,
      activeIcon: <ParameterIcon stroke={"var(--theme-secondary-color, #ED028C)"}
      />,
      onclick: () => onClickParametersTab,
    },
    {
      name: "Graphic",
      icon: <GraphicIcon />,
      activeIcon: <GraphicIcon stroke={"var(--theme-secondary-color, #ED028C)"}
      />,
      onclick: () => onClickGraphicTab,
    },
  ];

  const getProductByIdImmediate = useCallback(async () => {
    const rawId = router?.query?.id;
    const productId = Array.isArray(rawId) ? rawId[0] : rawId;
    if (!productId) {
      return;
    }
    const gen = ++fetchGenerationRef.current;
    const isTemplateEdit = router.asPath?.includes("/settings/templates/");
    const data = isTemplateEdit
      ? returnResult(
          await callApi(
            "GET",
            "/v1/printhouse-config/templates/get-template-by-id",
            { Id: productId },
          ),
          undefined,
        )
      : await getAndSetProductById(callApi, undefined, {
          Id: productId,
          sourceView: ESourceView.ProductEditView,
        });
    if (gen !== fetchGenerationRef.current) {
      return;
    }
    const previousRoot = templateRef.current;
    const mergedData = previousRoot
      ? preserveValuesConfigsHiddenOnRefetch(previousRoot, data)
      : data;
    templateRef.current = mergedData;
    setTemplate(mergedData);
    setProductState(mergedData);
  }, [router, callApi]);

  const scheduleDebouncedGetProductById = useCallback(() => {
    if (debounceRefetchTimerRef.current) {
      clearTimeout(debounceRefetchTimerRef.current);
    }
    debounceRefetchTimerRef.current = setTimeout(() => {
      debounceRefetchTimerRef.current = null;
      getProductByIdImmediate();
    }, PRODUCT_REFETCH_DEBOUNCE_MS);
  }, [getProductByIdImmediate]);

  const getProductById = getProductByIdImmediate;

  const [relatedParameters, setaRelatedParameters] = useState([]);

  useEffect(() => {
    const collectRelatedParameters = (template, result = []) => {
      if (template) {
        if (template?.relatedParameters) {
          result.push(...template.relatedParameters);
        }

        if (template?.sections) {
          for (const section of template?.sections) {
            collectRelatedParameters(section, result);
          }
        }

        if (template.subSections) {
          for (const subSection of template.subSections) {
            collectRelatedParameters(subSection, result);
          }
        }

        if (template.parameters) {
          for (const parameter of template.parameters) {
            collectRelatedParameters(parameter, result);
          }
        }

        return result;
      }
    };
    const allRelatedParameters = collectRelatedParameters(template);
    setaRelatedParameters(allRelatedParameters);
  }, [template]);

  useEffect(() => {
    getAllMaterial().then(() => {
      getProductByIdImmediate();
    });
  }, [router, getProductByIdImmediate, i18n.language]);

  const pendingTemplateParameterUpdatesRef = useRef(new Map<string, any>());
  const pendingTemplateStructureRef = useRef<any[]>([]);
  const [pendingTemplateParameterCount, setPendingTemplateParameterCount] = useState(0);
  const [savingTemplateParameters, setSavingTemplateParameters] = useState(false);

  const syncPendingTemplateCount = () => {
    setPendingTemplateParameterCount(
      pendingTemplateParameterUpdatesRef.current.size + pendingTemplateStructureRef.current.length
    );
  };

  const replaceTemplateState = (next: any) => {
    templateRef.current = next;
    setTemplate(next);
    setProductState(next);
  };

  const rememberTemplateParameterUpdate = useCallback((body: any) => {
    const parameterId = body?.parameter?.id;
    if (!parameterId) return;
    const key = `${body.sectionId}|${body.subSectionId || ""}|${parameterId}`;
    pendingTemplateParameterUpdatesRef.current.set(key, body);
    syncPendingTemplateCount();

    const apply = (root: any) => {
      if (!root?.sections) return root;
      const next = cloneDeep(root);
      const section = next.sections.find((item: any) => item.id === body.sectionId);
      const parameters = body.subSectionId
        ? section?.subSections?.find((item: any) => item.id === body.subSectionId)?.parameters
        : section?.parameters;
      const index = parameters?.findIndex((item: any) => item.id === parameterId);
      if (index >= 0) {
        parameters[index] = { ...parameters[index], ...body.parameter };
      }
      return next;
    };

    const source = templateRef.current;
    if (source) {
      const next = apply(source);
      templateRef.current = next;
      setTemplate(next);
      setProductState(next);
      return;
    }
    setProductState((prev) => {
      const next = apply(prev);
      templateRef.current = next;
      return next;
    });
  }, []);

  const savePendingTemplateParameters = useCallback(async () => {
    const updates = Array.from(pendingTemplateParameterUpdatesRef.current.entries());
    const structureRank = (url: string) => {
      if (url?.includes("/add-section")) return 0;
      if (url?.includes("/add-subsection")) return 1;
      if (url?.includes("/add-existing-parameter")) return 2;
      return 3;
    };
    pendingTemplateStructureRef.current.sort(
      (left, right) => structureRank(left.url) - structureRank(right.url)
    );
    const structure = [...pendingTemplateStructureRef.current];
    if ((!updates.length && !structure.length) || savingTemplateParameters) return;
    setSavingTemplateParameters(true);
    let failed = false;
    for (const change of structure) {
      const res = await callApi(
        change.method,
        change.url,
        change.method === "DELETE" ? undefined : change.body,
        false
      );
      if (!res?.success) {
        failed = true;
        break;
      }
      pendingTemplateStructureRef.current.shift();
    }
    if (!failed && updates.length) {
      const payload = updates.map(([, update]) => {
        const current = findParameterByIdInTemplate(templateRef.current, update?.parameter?.id);
        return {
          ...update,
          parameter: {
            ...(update?.parameter || {}),
            ...(current || {}),
            id: update?.parameter?.id,
            isHidden: current?.isHidden ?? update?.parameter?.isHidden,
            isRequired: current?.isRequired ?? update?.parameter?.isRequired,
            isLock: current?.isLock ?? update?.parameter?.isLock,
            isShowToClient: current?.isShowToClient ?? update?.parameter?.isShowToClient,
            aiSuggestionValues: current?.aiSuggestionValues ?? update?.parameter?.aiSuggestionValues,
            defaultValue: current?.defaultValue ?? update?.parameter?.defaultValue,
            valuesConfigs: current?.valuesConfigs ?? update?.parameter?.valuesConfigs,
            translations: current?.translations ?? update?.parameter?.translations,
            defaultUnit: current?.defaultUnit ?? update?.parameter?.defaultUnit,
            imperialUnit: current?.imperialUnit ?? update?.parameter?.imperialUnit,
          },
        };
      });
      const res = await callApi(
        "PUT",
        `/v1/printhouse-config/templates/update-template-parameters`,
        payload,
        false
      );
      const saved = res?.data?.data?.data;
      if (!res?.success || saved === false) {
        failed = true;
      }
    }
    setSavingTemplateParameters(false);
    if (!failed) {
      pendingTemplateParameterUpdatesRef.current.clear();
      pendingTemplateStructureRef.current = [];
      setPendingTemplateParameterCount(0);
      alertSuccessAdded();
      await getProductByIdImmediate();
    } else {
      syncPendingTemplateCount();
      alertFaultAdded();
    }
  }, [callApi, savingTemplateParameters, alertSuccessAdded, alertFaultAdded, getProductByIdImmediate]);

  const updateProductParameterEndPoint = useCallback(
    async (sectionId: string, subSectionId: string, data: any) => {
      const rawId = router?.query?.id;
      const productId = Array.isArray(rawId) ? rawId[0] : rawId;
      if (router.asPath?.includes("/settings/templates/")) {
        rememberTemplateParameterUpdate({
          productId,
          sectionId,
          subSectionId,
          productParameterType: 1,
          ...data,
        });
        return;
      }
      const queueKey = data?.parameter?.id ?? "__global_put__";

      await runSerializedParameterUpdate(queueKey, async () => {
        const res = await callApi(
          "PUT",
          router.asPath?.includes("/settings/templates/")
            ? `/v1/printhouse-config/templates/update-template-parameter`
            : `/v1/printhouse-config/products/update-product-parameter`,
          {
            productId,
            sectionId: sectionId,
            subSectionId: subSectionId,
            productParameterType: 1,
            ...data,
          }
        );
        if (res?.success) {
          alertSuccessAdded();
          scheduleDebouncedGetProductById();
        } else {
          alertFaultAdded();
        }
      });
    },
    [
      router,
      callApi,
      runSerializedParameterUpdate,
      scheduleDebouncedGetProductById,
      alertSuccessAdded,
      alertFaultAdded,
      rememberTemplateParameterUpdate,
    ]
  );

  const removeProductParamete = useCallback(
    async (
      sectionId: string,
      subSectionId: string,
      parameter: any,
      updateType: UpdateTypes
    ) => {
      const isTemplateEdit = router.asPath?.includes("/settings/templates/");
      const rawId = router?.query?.id;
      const entityId = Array.isArray(rawId) ? rawId[0] : rawId;
      if (isTemplateEdit) {
        const pendingAddIndex = pendingTemplateStructureRef.current.findIndex(
          (op) =>
            op.url?.includes("/add-existing-parameter") &&
            op.body?.parameterId === parameter?.id &&
            op.body?.sectionId === sectionId &&
            op.body?.subSectionId === subSectionId
        );
        if (pendingAddIndex >= 0) {
          pendingTemplateStructureRef.current.splice(pendingAddIndex, 1);
        } else {
          pendingTemplateStructureRef.current.push({
            method: "POST",
            url: `/v1/printhouse-config/templates/remove-parameter`,
            body: {
              templateId: entityId,
              sectionId,
              subSectionId,
              parameterId: parameter?.id,
            },
          });
        }
        const pendingKey = `${sectionId}|${subSectionId || ""}|${parameter?.id}`;
        pendingTemplateParameterUpdatesRef.current.delete(pendingKey);
        syncPendingTemplateCount();
        const root = cloneDeep(templateRef.current);
        const section = root?.sections?.find((item: any) => item.id === sectionId);
        const parameters = subSectionId
          ? section?.subSections?.find((item: any) => item.id === subSectionId)?.parameters
          : section?.parameters;
        if (parameters) {
          const index = parameters.findIndex((item: any) => item.id === parameter?.id);
          if (index >= 0) parameters.splice(index, 1);
        }
        replaceTemplateState(root);
        return;
      }
      const res = await callApi(
        isTemplateEdit ? "POST" : "PUT",
        isTemplateEdit
          ? `/v1/printhouse-config/templates/remove-parameter`
          : `/v1/printhouse-config/products/remove-parameter-from-product`,
        isTemplateEdit
          ? {
              templateId: entityId,
              sectionId: sectionId,
              subSectionId: subSectionId,
              parameterId: parameter?.id,
            }
          : {
          productId: router?.query?.id,
          sectionId: sectionId,
          subSectionId: subSectionId,
          parameterId: parameter?.id,
          updateType: updateType || 0,
        }
      );
      if (res?.success) {
        alertSuccessAdded();
        getProductByIdImmediate();
      } else {
        alertFaultAdded();
      }
    },
    [router, callApi, alertSuccessAdded, alertFaultAdded, getProductByIdImmediate]
  );

  const addSubSectionToProduct = useCallback(
    async (sectionId: string, subSectionName: string) => {
      const res = await callApi(
        "POST",
        `/v1/printhouse-config/products/add-subsection-to-product`,
        {
          productId: router?.query?.id,
          sectionId: sectionId,
          subSectionName: subSectionName,
        }
      );
      if (res?.success) {
        alertSuccessAdded();
        getProductByIdImmediate();
      } else {
        alertFaultAdded();
      }
    },
    [router, callApi, alertSuccessAdded, alertFaultAdded, getProductByIdImmediate]
  );

  const addSectionToTemplate = useCallback(
    async (section: any) => {
      const rawId = router?.query?.id;
      const templateId = Array.isArray(rawId) ? rawId[0] : rawId;
      const localSection = {
        ...section,
        id: section?.id || uuidv4(),
        parameters: section?.parameters || [],
        subSections: section?.subSections || [],
      };
      pendingTemplateStructureRef.current.push({
        method: "POST",
        url: `/v1/printhouse-config/templates/add-section`,
        body: { templateId, section: localSection },
      });
      syncPendingTemplateCount();
      const root = cloneDeep(templateRef.current) || { sections: [] };
      root.sections = [...(root.sections || []), localSection];
      replaceTemplateState(root);
    },
    [router]
  );

  const queueAddExistingParameterToTemplate = useCallback(
    (sectionId: string, subSectionId: string, parameter: any) => {
      const rawId = router?.query?.id;
      const templateId = Array.isArray(rawId) ? rawId[0] : rawId;
      const root = cloneDeep(templateRef.current);
      const section = root?.sections?.find((item: any) => item.id === sectionId);
      const subSection = section?.subSections?.find((item: any) => item.id === subSectionId);
      if (!subSection || !parameter?.id) return false;
      subSection.parameters = subSection.parameters || [];
      if (subSection.parameters.some((item: any) => item.id === parameter.id)) return false;
      pendingTemplateStructureRef.current.push({
        method: "POST",
        url: `/v1/printhouse-config/templates/add-existing-parameter`,
        body: {
          templateId,
          sectionId,
          subSectionId,
          parameterId: parameter.id,
        },
      });
      subSection.parameters.push({
        ...parameter,
        parameterType: parameter.parameterType ?? parameter.type,
        isHidden: parameter.isHidden ?? false,
        isRequired: parameter.isRequired ?? false,
        isLock: parameter.isLock ?? false,
        isShowToClient: parameter.isShowToClient ?? true,
        valuesConfigs: parameter.valuesConfigs ?? parameter.values ?? [],
      });
      syncPendingTemplateCount();
      replaceTemplateState(root);
      return true;
    },
    [router]
  );

  const addSubSectionToTemplate = useCallback(
    async (sectionId: string, subSection: any) => {
      const rawId = router?.query?.id;
      const templateId = Array.isArray(rawId) ? rawId[0] : rawId;
      const localSubSection = {
        ...subSection,
        id: subSection?.id || uuidv4(),
        parameters: subSection?.parameters || [],
      };
      pendingTemplateStructureRef.current.push({
        method: "POST",
        url: `/v1/printhouse-config/templates/add-subsection`,
        body: { templateId, sectionId, subSection: localSubSection },
      });
      syncPendingTemplateCount();
      const root = cloneDeep(templateRef.current);
      const section = root?.sections?.find((item: any) => item.id === sectionId);
      if (section) {
        section.subSections = [...(section.subSections || []), localSubSection];
        replaceTemplateState(root);
      }
    },
    [router]
  );

  const templateIdFromRoute = () => {
    const rawId = router?.query?.id;
    return Array.isArray(rawId) ? rawId[0] : rawId;
  };

  const updateSectionOnTemplate = useCallback((section: any) => {
    const templateId = templateIdFromRoute();
    const pendingAdd = pendingTemplateStructureRef.current.find(
      (op) => op.url?.includes("/add-section") && op.body?.section?.id === section.id
    );
    if (pendingAdd) {
      pendingAdd.body.section = {
        ...pendingAdd.body.section,
        ...section,
        parameters: pendingAdd.body.section.parameters,
        subSections: pendingAdd.body.section.subSections,
      };
    } else {
      const index = pendingTemplateStructureRef.current.findIndex(
        (op) => op.url?.includes("/update-section") && op.body?.section?.id === section.id
      );
      const operation = {
        method: "PUT",
        url: `/v1/printhouse-config/templates/update-section`,
        body: { templateId, section },
      };
      if (index >= 0) pendingTemplateStructureRef.current[index] = operation;
      else pendingTemplateStructureRef.current.push(operation);
    }
    syncPendingTemplateCount();
    const root = cloneDeep(templateRef.current);
    const current = root?.sections?.find((item: any) => item.id === section.id);
    if (current) {
      Object.assign(current, section, {
        parameters: current.parameters,
        subSections: current.subSections,
      });
      replaceTemplateState(root);
    }
  }, [router]);

  const updateSubSectionOnTemplate = useCallback((sectionId: string, subSection: any) => {
    const templateId = templateIdFromRoute();
    const pendingAdd = pendingTemplateStructureRef.current.find(
      (op) => op.url?.includes("/add-subsection") && op.body?.subSection?.id === subSection.id
    );
    if (pendingAdd) {
      pendingAdd.body.subSection = {
        ...pendingAdd.body.subSection,
        ...subSection,
        parameters: pendingAdd.body.subSection.parameters,
      };
    } else {
      const index = pendingTemplateStructureRef.current.findIndex(
        (op) => op.url?.includes("/update-subsection") && op.body?.subSection?.id === subSection.id
      );
      const operation = {
        method: "PUT",
        url: `/v1/printhouse-config/templates/update-subsection`,
        body: { templateId, sectionId, subSection },
      };
      if (index >= 0) pendingTemplateStructureRef.current[index] = operation;
      else pendingTemplateStructureRef.current.push(operation);
    }
    syncPendingTemplateCount();
    const root = cloneDeep(templateRef.current);
    const section = root?.sections?.find((item: any) => item.id === sectionId);
    const current = section?.subSections?.find((item: any) => item.id === subSection.id);
    if (current) {
      Object.assign(current, subSection, { parameters: current.parameters });
      replaceTemplateState(root);
    }
  }, [router]);

  const deleteSectionFromTemplate = useCallback((sectionId: string) => {
    const templateId = templateIdFromRoute();
    const wasPendingAdd = pendingTemplateStructureRef.current.some(
      (op) => op.url?.includes("/add-section") && op.body?.section?.id === sectionId
    );
    pendingTemplateStructureRef.current = pendingTemplateStructureRef.current.filter(
      (op) => op.body?.section?.id !== sectionId && op.body?.sectionId !== sectionId
    );
    if (!wasPendingAdd) {
      pendingTemplateStructureRef.current.push({
        method: "DELETE",
        url: `/v1/printhouse-config/templates/delete-section?templateId=${templateId}&sectionId=${sectionId}`,
      });
    }
    pendingTemplateParameterUpdatesRef.current.forEach((_value, key) => {
      if (key.startsWith(`${sectionId}|`)) pendingTemplateParameterUpdatesRef.current.delete(key);
    });
    syncPendingTemplateCount();
    const root = cloneDeep(templateRef.current);
    root.sections = (root.sections || []).filter((item: any) => item.id !== sectionId);
    replaceTemplateState(root);
  }, [router]);

  const deleteSubSectionFromTemplate = useCallback((sectionId: string, subSectionId: string) => {
    const templateId = templateIdFromRoute();
    const wasPendingAdd = pendingTemplateStructureRef.current.some(
      (op) => op.url?.includes("/add-subsection") && op.body?.subSection?.id === subSectionId
    );
    pendingTemplateStructureRef.current = pendingTemplateStructureRef.current.filter(
      (op) => op.body?.subSection?.id !== subSectionId && op.body?.subSectionId !== subSectionId
    );
    if (!wasPendingAdd) {
      pendingTemplateStructureRef.current.push({
        method: "DELETE",
        url: `/v1/printhouse-config/templates/delete-sub-section?templateId=${templateId}&sectionId=${sectionId}&subSectionId=${subSectionId}`,
      });
    }
    pendingTemplateParameterUpdatesRef.current.forEach((_value, key) => {
      if (key.startsWith(`${sectionId}|${subSectionId}|`)) pendingTemplateParameterUpdatesRef.current.delete(key);
    });
    syncPendingTemplateCount();
    const root = cloneDeep(templateRef.current);
    const section = root?.sections?.find((item: any) => item.id === sectionId);
    if (section) {
      section.subSections = (section.subSections || []).filter((item: any) => item.id !== subSectionId);
      replaceTemplateState(root);
    }
  }, [router]);

  const saveTemplateParameterSettings = useCallback(
    async (sectionId: string, subSectionId: string, parameter: any) => {
      await updateProductParameterEndPoint(sectionId, subSectionId, {
        parameter,
        updateType: UpdateTypes.PRODUCT_ONLY,
      });
    },
    [updateProductParameterEndPoint]
  );

  const updatedProductParameterTranslations = useCallback(
    async (
      sectionId: string,
      subSectionId: string,
      parameter: any,
      translations: { lang: string; name: string }[]
    ) => {
      await updateProductParameterEndPoint(sectionId, subSectionId, {
        parameter: {
          ...parameter,
          translations,
        },
        updateType: UpdateTypes.PRODUCT_ONLY,
      });
    },
    [updateProductParameterEndPoint]
  );

  const updatedProductParameterShowToClient = useCallback(
    async (
      sectionId: string,
      subSectionId: string,
      parameter: any,
      updateType: UpdateTypes
    ) => {
      await updateProductParameterEndPoint(sectionId, subSectionId, {
        parameter: {
          ...parameter,
          isShowToClient: !parameter?.isShowToClient,
        },
        actionEnum: parameter?.isShowToClient
          ? ProductLogActionEnum.UpdateParameterToHideFromDetails
          : ProductLogActionEnum.UpdateParameterToShowInDetails,
        updatedActionType: EUpdateActionType.showToClient,
        updateType,
      });
    },
    [updateProductParameterEndPoint]
  );

  const updatedProductParameterHidden = useCallback(
    async (
      sectionId: string,
      subSectionId: string,
      parameter: any,
      updateType: UpdateTypes
    ) => {
      await updateProductParameterEndPoint(sectionId, subSectionId, {
        parameter: {
          ...parameter,
          isHidden: !parameter?.isHidden,
        },
        actionEnum: parameter?.isHidden
          ? ProductLogActionEnum.UpdateParameterToShown
          : ProductLogActionEnum.UpdateParameterToHidden,
        updatedActionType: EUpdateActionType.hidden,
        updateType,
      });
    },
    [updateProductParameterEndPoint]
  );

  const updatedProductParameteRequierd = useCallback(
    async (
      sectionId: string,
      subSectionId: string,
      parameter: any,
      updateType: UpdateTypes
    ) => {
      await updateProductParameterEndPoint(sectionId, subSectionId, {
        parameter: {
          ...parameter,
          isRequired: !parameter?.isRequired,
        },
        actionEnum: parameter?.isRequired
          ? ProductLogActionEnum.UpdateParameterToNotRequired
          : ProductLogActionEnum.UpdateParameterToRequired,
        updatedActionType: EUpdateActionType.required,
        updateType,
      });
    },
    [updateProductParameterEndPoint]
  );

  const updatedProductParameteLocked = useCallback(
    async (
      sectionId: string,
      subSectionId: string,
      parameter: any,
      updateType: UpdateTypes
    ) => {
      await updateProductParameterEndPoint(sectionId, subSectionId, {
        parameter: {
          ...parameter,
          isLock: !parameter?.isLock,
        },
        actionEnum: parameter?.isLock
          ? ProductLogActionEnum.UpdateParameterToEditable
          : ProductLogActionEnum.UpdateParameterToNotEditable,
        updatedActionType: EUpdateActionType.lock,
        updateType,
      });
    },
    [updateProductParameterEndPoint]
  );

  const updatedProductParameteName = useCallback(
    async (sectionId: string, subSectionId: string, parameter: any) => {
      if (changeName !== parameter?.name && changeName?.length) {
        await updateProductParameterEndPoint(sectionId, subSectionId, {
          parameter: {
            ...parameter,
            name: changeName?.length ? changeName : parameter.name,
          },
          actionEnum: ProductLogActionEnum.UpdateParameterDefaultValue,
        });
        setChangeName("");
      }
    },
    [updateProductParameterEndPoint, changeName]
  );
  const updatedProductParameteDefaultValue = useCallback(
    async (sectionId: string, subSectionId: string, parameter: any) => {
      if (changeDefaultValue !== parameter?.defaultValue) {
        await updateProductParameterEndPoint(sectionId, subSectionId, {
          parameter: {
            ...parameter,
            defaultValue: changeDefaultValue,
          },
          actionEnum: ProductLogActionEnum.UpdateParameterDefaultValue,
        });
        setChangeDefaultValue("");
      }
    },

    [updateProductParameterEndPoint, changeDefaultValue]
  );
  const updatedProductParameteDefaultValueForSwitch = useCallback(
    async (
      sectionId: string,
      subSectionId: string,
      parameter: any,
      value: boolean
    ) => {
      await updateProductParameterEndPoint(sectionId, subSectionId, {
        parameter: {
          ...parameter,
          defaultValue: value.toString(),
        },
        actionEnum: ProductLogActionEnum.UpdateParameterDefaultValue,
      });
    },
    [updateProductParameterEndPoint]
  );

  const updatedProductParameterValuesConfigsDefault = useCallback(
    async (
      sectionId: string,
      subSectionId: string,
      parameter: any,
      option: any
    ) => {
      const parameterId = parameter?.id;
      if (!parameterId) {
        return;
      }

      const objectIdToUpdate = option?.id;
      const isClearingDefault =
        option == null ||
        objectIdToUpdate == null ||
        objectIdToUpdate === "";

      const rawId = router?.query?.id;
      const productId = Array.isArray(rawId) ? rawId[0] : rawId;

      await runSerializedParameterUpdate(parameterId, async () => {
        const root = templateRef.current;
        const draft = visibilityDraftRef.current;
        const sourceConfigs =
          draft?.parameterId === parameterId &&
            visibilityDraftDirtyRef.current
            ? cloneDeep(draft.valuesConfigs)
            : cloneValuesConfigsForParameter(templateRef.current, parameter);

        const updatedArray = isClearingDefault
          ? sourceConfigs.map((obj) => ({ ...obj, isDefault: false }))
          : sourceConfigs.map((obj) => {
            if (obj.id === objectIdToUpdate) {
              return { ...obj, isDefault: true };
            }
            return { ...obj, isDefault: false };
          });

        const base =
          (root && findParameterByIdInTemplate(root, parameterId)) || parameter;

        visibilityDraftRef.current = null;
        visibilityDraftDirtyRef.current = false;
        visibilityPaperContextRef.current = null;

        if (root) {
          const merged = mergeParameterValuesConfigsInTemplate(
            cloneDeep(root),
            parameterId,
            updatedArray
          );
          templateRef.current = merged;
          setTemplate(merged);
          setProductState(merged);
        }

        const parameterPayload = isClearingDefault
          ? { ...base, defaultValue: null, valuesConfigs: updatedArray }
          : { ...base, valuesConfigs: updatedArray };

        if (router.asPath?.includes("/settings/templates/")) {
          rememberTemplateParameterUpdate({
            productId,
            sectionId,
            subSectionId,
            productParameterType: 1,
            parameter: parameterPayload,
            actionEnum: ProductLogActionEnum.UpdateParameterDefaultValue,
          });
          return;
        }
        const res = await callApi(
          "PUT",
          `/v1/printhouse-config/products/update-product-parameter`,
          {
            productId,
            sectionId,
            subSectionId,
            productParameterType: 1,
            parameter: parameterPayload,
            actionEnum: ProductLogActionEnum.UpdateParameterDefaultValue,
          }
        );
        if (res?.success) {
          alertSuccessAdded();
          scheduleDebouncedGetProductById();
        } else {
          alertFaultAdded();
          await getProductByIdImmediate();
        }
      });
    },
    [
      router,
      callApi,
      runSerializedParameterUpdate,
      scheduleDebouncedGetProductById,
      getProductByIdImmediate,
      alertSuccessAdded,
      alertFaultAdded,
      rememberTemplateParameterUpdate,
    ]
  );
  const updatedParameterMaterialTypeValuesConfigsDefault = useCallback(
    async (
      sectionId: string,
      subSectionId: string,
      parameter: any,
      option: any
    ) => {
      let temp = [...parameter?.valuesConfigs];
      if (temp?.length <= 0) {
        temp.push({
          id: uuidv4(),
          isHidden: false,
          isDefault: true,
          isDeleted: false,
          value: option?.value,
          materialValueIds: [
            {
              path: option?.pathName,
              valueId: option?.valueId,
              value: option?.value,
            },
          ],
        });
        await updatedValuesConfigsForParameters(sectionId, subSectionId, {
          ...parameter,
          defaultValue: option?.valueId ?? null,
          valuesConfigs: temp,
        }, undefined, undefined, undefined, ProductLogActionEnum.UpdateParameterDefaultValue);
      } else {
        let objectIdToUpdate = option?.valueId;
        if (!objectIdToUpdate) {
          // Case where the option is deleted and no new option is selected
          const updatedArray = temp.map((obj) => ({
            ...obj,
            isDefault: false,
          }));
          await updateProductParameterEndPoint(sectionId, subSectionId, {
            parameter: {
              ...parameter,
              defaultValue: null,
              valuesConfigs: updatedArray,
            },
            actionEnum: ProductLogActionEnum.UpdateParameterDefaultValue,
          });
        } else {
          if (
            temp.findIndex(
              (item) => item?.materialValueIds[0]?.valueId === objectIdToUpdate
            ) !== -1
          ) {
            const updatedArray = temp.map((obj) => {
              if (obj.materialValueIds[0]?.valueId === objectIdToUpdate) {
                return {
                  ...obj,
                  isDefault: true,
                  value: option?.value,
                  materialValueIds: [
                    {
                      path: option?.pathName,
                      valueId: option?.valueId,
                      value: option?.value,
                    },
                  ],
                };
              } else {
                return { ...obj, isDefault: false };
              }
            });
            await updateProductParameterEndPoint(sectionId, subSectionId, {
              parameter: {
                ...parameter,
                defaultValue: option?.valueId ?? null,
                valuesConfigs: updatedArray,
              },
              actionEnum: ProductLogActionEnum.UpdateParameterDefaultValue,
            });
          } else {
            temp.push({
              id: uuidv4(),
              isHidden: false,
              isDefault: true,
              isDeleted: false,
              value: option?.value,
              materialValueIds: [
                {
                  path: option?.pathName,
                  valueId: option?.valueId,
                  value: option?.value,
                },
              ],
            });
            const updatedArray = temp
              .filter(
                (value) =>
                  !(
                    value?.materialValueIds?.length === 1 &&
                    value?.materialValueIds[0]?.path === null &&
                    value?.materialValueIds[0]?.valueId === null
                  )
              )
              .map((obj) => {
                if (obj.materialValueIds[0]?.valueId === objectIdToUpdate) {
                  return { ...obj, isDefault: true };
                } else {
                  return { ...obj, isDefault: false };
                }
              });
            await updatedValuesConfigsForParameters(sectionId, subSectionId, {
              ...parameter,
              defaultValue: option?.valueId ?? null,
              valuesConfigs: updatedArray,
            }, undefined, undefined, undefined, ProductLogActionEnum.UpdateParameterDefaultValue);
          }
        }
      }
    },
    [router]
  );

  const updatedValuesConfigsForParameters = useCallback(
    async (
      sectionId: string,
      subSectionId: string,
      data: any,
      updatedValueId?: string,
      updatedActionType?: EUpdateActionType,
      updateType?: UpdateTypes,
      actionEnum?: ProductLogActionEnum,
      updatedMaterialValueId?: string
    ) => {
      const parameterId = data?.id;
      const rawId = router?.query?.id;
      const productId = Array.isArray(rawId) ? rawId[0] : rawId;

      const sendPut = async (payload: any) => {
        if (router.asPath?.includes("/settings/templates/")) {
          rememberTemplateParameterUpdate({
            productId,
            sectionId,
            subSectionId,
            productParameterType: 1,
            parameter: payload,
            updatedValueId,
            updatedMaterialValueId,
            updatedActionType,
            updateType,
            actionEnum,
          });
          return;
        }
        const res = await callApi(
          "PUT",
          `/v1/printhouse-config/products/update-product-parameter`,
          {
            productId,
            sectionId: sectionId,
            subSectionId: subSectionId,
            productParameterType: 1,
            parameter: payload,
            updatedValueId,
            updatedMaterialValueId,
            updatedActionType,
            updateType,
            actionEnum,
          }
        );
        if (res?.success) {
          alertSuccessAdded();
          scheduleDebouncedGetProductById();
        } else {
          alertFaultAdded();
          await getProductByIdImmediate();
        }
      };

      if (!parameterId) {
        await sendPut(data);
        return;
      }

      await runSerializedParameterUpdate(parameterId, async () => {
        const root = templateRef.current;
        let payload = data;
        if (root) {
          const fresh = findParameterByIdInTemplate(root, parameterId);
          if (fresh) {
            payload = { ...fresh, ...data, valuesConfigs: data.valuesConfigs };
          }
        }
        if (root && payload?.valuesConfigs) {
          const merged = mergeParameterValuesConfigsInTemplate(
            cloneDeep(root),
            parameterId,
            payload.valuesConfigs
          );
          templateRef.current = merged;
          setTemplate(merged);
          setProductState(merged);
        }
        await sendPut(payload);
      });
    },
    [
      router,
      callApi,
      runSerializedParameterUpdate,
      scheduleDebouncedGetProductById,
      getProductByIdImmediate,
      alertSuccessAdded,
      alertFaultAdded,
      rememberTemplateParameterUpdate,
    ]
  );

  const [visibilityDraft, setVisibilityDraft] = useState<{
    parameterId: string;
    sectionId: string;
    subSectionId: string;
    valuesConfigs: any[];
    isMaterial: boolean;
    updateType?: UpdateTypes;
  } | null>(null);
  const [visibilityDraftDirty, setVisibilityDraftDirty] = useState(false);
  useEffect(() => {
    visibilityDraftDirtyRef.current = visibilityDraftDirty;
  }, [visibilityDraftDirty]);

  useEffect(() => {
    visibilityDraftRef.current = visibilityDraft;
  }, [visibilityDraft]);

  useEffect(() => {
    if (!visibilityDraftRef.current) {
      setVisibilityDraft(null);
      setVisibilityDraftDirty(false);
    }
  }, [template]);

  useEffect(() => {
    setVisibilityDraft(null);
    setVisibilityDraftDirty(false);
    visibilityPaperContextRef.current = null;
  }, [i18n.language]);

  const [visibilitySaveLoading, setVisibilitySaveLoading] = useState(false);
  /** آخر قائمة مواد معروضة لكل parameter (نفس `options` في الـ Autocomplete) لدمجها مع valuesConfigs عند تبديل الظهور. */
  const materialListOptionsByParameterIdRef = useRef(
    new Map<string, any[]>()
  );
  const materialVisibilityListScrollRef = useRef<number | null>(null);
  const captureVisibilityDropdownScroll = (e: MouseEvent) => {
    const listbox = (e.currentTarget as HTMLElement).closest(
      "ul.MuiAutocomplete-listbox"
    ) as HTMLUListElement | null;
    if (listbox) {
      materialVisibilityListScrollRef.current = listbox.scrollTop;
    }
  };
  /** يمنع `openVisibilityDraftSession` من عدم إعادة وضع dirty عند إعادة onOpen لنفس الـ parameter. */
  const openVisibilityReusedSessionRef = useRef(false);

  useLayoutEffect(() => {
    if (materialVisibilityListScrollRef.current == null) {
      return;
    }
    const top = materialVisibilityListScrollRef.current;
    const tryApply = (attempt: number) => {
      const el = document.querySelector(
        "ul.MuiAutocomplete-listbox"
      ) as HTMLUListElement | null;
      if (el) {
        el.scrollTop = top;
        materialVisibilityListScrollRef.current = null;
        return;
      }
      if (attempt < 8) {
        requestAnimationFrame(() => tryApply(attempt + 1));
      } else {
        materialVisibilityListScrollRef.current = null;
      }
    };
    tryApply(0);
  }, [visibilityDraft, visibilityDraftDirty]);

  const toggleStandardVisibilityInDraft = useCallback(
    (
      sectionId: string,
      subSectionId: string,
      parameter: any,
      option: any,
      updateType?: UpdateTypes
    ) => {
      const parameterId = parameter?.id;
      const objectIdToUpdate = option?.id;
      if (!parameterId || !objectIdToUpdate) {
        return;
      }

      setVisibilityDraft((prev) => {
        const source =
          prev && prev.parameterId === parameterId
            ? cloneDeep(prev.valuesConfigs)
            : cloneValuesConfigsForParameter(templateRef.current, parameter);
        const nextConfigs = source.map((obj: any) =>
          obj.id === objectIdToUpdate
            ? { ...obj, isHidden: !obj.isHidden }
            : obj
        );
        return {
          parameterId,
          sectionId,
          subSectionId,
          valuesConfigs: nextConfigs,
          isMaterial: false,
          updateType: updateType ?? prev?.updateType,
        };
      });
      setVisibilityDraftDirty(true);
    },
    []
  );

  const toggleMaterialVisibilityInDraft = useCallback(
    (
      sectionId: string,
      subSectionId: string,
      parameter: any,
      option: any,
      updateType?: UpdateTypes
    ) => {
      const parameterId = parameter?.id;
      if (!parameterId) {
        return;
      }
      const objectIdToUpdate = resolveMaterialOptionValueId(option);
      if (objectIdToUpdate == null || objectIdToUpdate === "") {
        return;
      }

      setVisibilityDraft((prev) => {
        const raw = cloneDeep(
          prev && prev.parameterId === parameterId
            ? prev.valuesConfigs
            : cloneValuesConfigsForParameter(templateRef.current, parameter)
        );
        const listOpts = materialListOptionsByParameterIdRef.current.get(
          parameterId
        );
        const temp = mergeVisibleMaterialListIntoValuesConfigs(
          raw,
          listOpts,
          isVariationProductMode
        );
        let next = temp;

        if (!temp || temp.length <= 0) {
          const newId = uuidv4();
          next = [
            {
              id: newId,
              isHidden: true,
              isDefault: false,
              isDeleted: false,
              value: option?.value,
              materialValueIds: [
                {
                  path: option?.pathName,
                  valueId: objectIdToUpdate,
                  value: option?.value,
                },
              ],
            },
          ];
        } else {
          const matchingItem = temp.find((item) =>
            materialValueConfigMatchesValueId(item, objectIdToUpdate)
          );
          if (matchingItem) {
            next = temp.map((obj) => {
              if (materialValueConfigMatchesValueId(obj, objectIdToUpdate)) {
                return { ...obj, isHidden: !obj.isHidden };
              }
              return obj;
            });
          } else {
            const newId = uuidv4();
            next = [
              ...temp,
              {
                id: newId,
                isHidden: true,
                isDefault: false,
                isDeleted: false,
                value: option?.value,
                materialValueIds: [
                  {
                    path: option?.pathName,
                    valueId: objectIdToUpdate,
                    value: option?.value,
                  },
                ],
              },
            ];
          }
        }

        return {
          parameterId,
          sectionId,
          subSectionId,
          valuesConfigs: next,
          isMaterial: true,
          updateType: updateType ?? prev?.updateType,
        };
      });
      setVisibilityDraftDirty(true);
    },
    [isVariationProductMode]
  );

  const persistVisibilityToggleNow = useCallback(
    async (
      isMaterial: boolean,
      sectionId: string,
      subSectionId: string,
      parameter: any,
      option: any,
      resolvedUpdateType: UpdateTypes
    ) => {
      const parameterId = parameter?.id;
      if (!parameterId) {
        return;
      }

      const prevDraft =
        visibilityDraft?.parameterId === parameterId ? visibilityDraft : null;

      const base = parameter;

      let nextConfigs: any[];

      if (!isMaterial) {
        const objectIdToUpdate = option?.id;
        if (!objectIdToUpdate) {
          return;
        }
        const source =
          prevDraft &&
            prevDraft.parameterId === parameterId &&
            visibilityDraftDirtyRef.current
            ? cloneDeep(prevDraft.valuesConfigs)
            : cloneValuesConfigsForParameter(templateRef.current, parameter);
        nextConfigs = source.map((obj: any) =>
          obj.id === objectIdToUpdate
            ? { ...obj, isHidden: !obj.isHidden }
            : obj
        );
      } else {
        const objectIdToUpdate = resolveMaterialOptionValueId(option);
        if (objectIdToUpdate == null || objectIdToUpdate === "") {
          return;
        }
        const raw = cloneDeep(
          prevDraft &&
            prevDraft.parameterId === parameterId &&
            visibilityDraftDirtyRef.current
            ? prevDraft.valuesConfigs
            : cloneValuesConfigsForParameter(templateRef.current, parameter)
        );
        const listOpts = materialListOptionsByParameterIdRef.current.get(
          parameterId
        );
        const temp = mergeVisibleMaterialListIntoValuesConfigs(
          raw,
          listOpts,
          isVariationProductMode
        );

        if (!temp || temp.length <= 0) {
          const newId = uuidv4();
          nextConfigs = [
            {
              id: newId,
              isHidden: true,
              isDefault: false,
              isDeleted: false,
              value: option?.value,
              materialValueIds: [
                {
                  path: option?.pathName,
                  valueId: objectIdToUpdate,
                  value: option?.value,
                },
              ],
            },
          ];
        } else {
          const matchingItem = temp.find((item) =>
            materialValueConfigMatchesValueId(item, objectIdToUpdate)
          );
          if (matchingItem) {
            nextConfigs = temp.map((obj) => {
              if (materialValueConfigMatchesValueId(obj, objectIdToUpdate)) {
                return { ...obj, isHidden: !obj.isHidden };
              }
              return obj;
            });
          } else {
            const newId = uuidv4();
            nextConfigs = [
              ...temp,
              {
                id: newId,
                isHidden: true,
                isDefault: false,
                isDeleted: false,
                value: option?.value,
                materialValueIds: [
                  {
                    path: option?.pathName,
                    valueId: objectIdToUpdate,
                    value: option?.value,
                  },
                ],
              },
            ];
          }
        }
      }

      const data = {
        ...base,
        valuesConfigs: nextConfigs,
      };

      await updatedValuesConfigsForParameters(
        sectionId,
        subSectionId,
        data,
        isMaterial ? undefined : (option?.id ?? undefined),
        EUpdateActionType.hiddenValue,
        resolvedUpdateType,
        ProductLogActionEnum.UpdateParameterDefaultValue,
        isMaterial ? (option?.valueId ?? undefined) : undefined
      );

      setVisibilityDraft(null);
      setVisibilityDraftDirty(false);
      if (visibilityPaperContextRef.current === parameterId) {
        visibilityPaperContextRef.current = null;
      }
    },
    [visibilityDraft, updatedValuesConfigsForParameters, isVariationProductMode]
  );

  const persistAiSuggestionToggleNow = useCallback(
    async (
      isMaterial: boolean,
      sectionId: string,
      subSectionId: string,
      parameter: any,
      option: any,
      resolvedUpdateType: UpdateTypes
    ) => {
      const parameterId = parameter?.id;
      if (!parameterId) {
        return;
      }

      const prevDraft =
        visibilityDraft?.parameterId === parameterId ? visibilityDraft : null;
      const base = parameter;
      let nextConfigs: any[];

      if (!isMaterial) {
        const objectIdToUpdate = option?.id;
        if (!objectIdToUpdate) {
          return;
        }
        const source =
          prevDraft &&
            prevDraft.parameterId === parameterId &&
            visibilityDraftDirtyRef.current
            ? cloneDeep(prevDraft.valuesConfigs)
            : cloneValuesConfigsForParameter(templateRef.current, parameter);
        nextConfigs = source.map((obj: any) =>
          obj.id === objectIdToUpdate
            ? { ...obj, isAiSuggestion: !obj.isAiSuggestion }
            : obj
        );
      } else {
        const objectIdToUpdate = resolveMaterialOptionValueId(option);
        if (objectIdToUpdate == null || objectIdToUpdate === "") {
          return;
        }
        const raw = cloneDeep(
          prevDraft &&
            prevDraft.parameterId === parameterId &&
            visibilityDraftDirtyRef.current
            ? prevDraft.valuesConfigs
            : cloneValuesConfigsForParameter(templateRef.current, parameter)
        );
        const listOpts = materialListOptionsByParameterIdRef.current.get(
          parameterId
        );
        const temp = mergeVisibleMaterialListIntoValuesConfigs(
          raw,
          listOpts,
          isVariationProductMode
        );

        if (!temp || temp.length <= 0) {
          const newId = uuidv4();
          nextConfigs = [
            {
              id: newId,
              isHidden: false,
              isAiSuggestion: true,
              isDefault: false,
              isDeleted: false,
              value: option?.value,
              materialValueIds: [
                {
                  path: option?.pathName,
                  valueId: objectIdToUpdate,
                  value: option?.value,
                },
              ],
            },
          ];
        } else {
          const matchingItem = temp.find((item) =>
            materialValueConfigMatchesValueId(item, objectIdToUpdate)
          );
          if (matchingItem) {
            nextConfigs = temp.map((obj) => {
              if (materialValueConfigMatchesValueId(obj, objectIdToUpdate)) {
                return { ...obj, isAiSuggestion: !obj.isAiSuggestion };
              }
              return obj;
            });
          } else {
            const newId = uuidv4();
            nextConfigs = [
              ...temp,
              {
                id: newId,
                isHidden: false,
                isAiSuggestion: true,
                isDefault: false,
                isDeleted: false,
                value: option?.value,
                materialValueIds: [
                  {
                    path: option?.pathName,
                    valueId: objectIdToUpdate,
                    value: option?.value,
                  },
                ],
              },
            ];
          }
        }
      }

      await updatedValuesConfigsForParameters(
        sectionId,
        subSectionId,
        { ...base, valuesConfigs: nextConfigs },
        isMaterial ? undefined : (option?.id ?? undefined),
        EUpdateActionType.aiSuggestion,
        resolvedUpdateType,
        ProductLogActionEnum.UpdateParameterDefaultValue,
        isMaterial ? (option?.valueId ?? undefined) : undefined
      );
    },
    [visibilityDraft, updatedValuesConfigsForParameters, isVariationProductMode]
  );

  const [openModal, setOpenModal] = useState(false);
  const [selectedParameter, setSelectedParameter] = useState<any>({});
  const [selectedSectonId, setSelectedSectonId] = useState({});
  const [selectedSubSection, setSelectedSubSection] = useState({});

  const onCloseModal = () => {
    setSelectedParameter({});
    setOpenModal(false);
  };

  const onOpenModal = (parameter, sectionId, subSectionId) => {
    setSelectedParameter(parameter);
    setSelectedSectonId(sectionId);
    setSelectedSubSection(subSectionId);
    setTimeout(() => {
      setOpenModal(true);
    }, 100);
  };

  const [openExternalIdsModal, setOpenExternalIdsModal] = useState(false);

  const onOpenExternalIdsModal = (
    parameter,
    subSectionParameters,
    sectionId,
    subSectionId
  ) => {
    if (!parameter?.productParametersExternalValues) {
      parameter.productParametersExternalValues = [];
    }
    //Check if parameterType is material
    const isMaterial =
      parameter?.parameterType === EParameterTypes.SELECT_MATERIALS ||
      parameter?.parameterType ===
      EParameterTypes.ADVERTISING_PRODUCT_CATEGORY ||
      parameter?.parameterType === EParameterTypes.ADVERTISING_PRODUCT_NAME;
    const isSwitch = parameter?.parameterType === EParameterTypes.SWITCH;
    const isInput =
      parameter?.parameterType === EParameterTypes.INPUT_TEXT ||
      parameter?.parameterType === EParameterTypes.INPUT_NUMBER;
    // Creating new valuesConfigs for materials
    let materialsOptions = [];
    const materialPath = parameter?.materialPath;
    const currentMaterialPath = Array.isArray(materialPath)
      ? materialPath[materialPath.length - 1]
      : undefined;
    const parentMaterialPath = Array.isArray(materialPath)
      ? materialPath.slice(0, -1)
      : [];
    let allMaterialsCopy = cloneDeep(allMaterials);

    if (isMaterial && Array.isArray(materialPath) && materialPath.length > 0) {
      if (parentMaterialPath && parentMaterialPath.length > 0) {
        allMaterialsCopy = allMaterialsCopy?.find((material) =>
          compareStrings(material.pathName, parentMaterialPath[0])
        );
        for (let i = 0; i < parentMaterialPath.length; i++) {
          const currentPath = parentMaterialPath.slice(0, i + 1).toString();
          const parentParameter = subSectionParameters.find(
            (x) => x.materialPath && x.materialPath.toString() == currentPath
          );
          if (parentParameter) {
            const parentParameterDefaultValueConfig =
              parentParameter?.valuesConfigs?.find(
                (item) => item.isDefault === true
              );
            if (parentParameterDefaultValueConfig) {
              const parentParameterValue =
                parentParameterDefaultValueConfig.materialValueIds[0].valueId;
              allMaterialsCopy = allMaterialsCopy?.data.find(
                (x) => x.valueId === parentParameterValue
              );
            } else {
              allMaterialsCopy = {
                data: [],
              };
            }
          }
        }
        if (allMaterialsCopy) {
          materialsOptions = [];
          if (allMaterialsCopy && allMaterialsCopy.data) {
            materialsOptions.push(...allMaterialsCopy.data);
          }
        }
      } else {
        allMaterialsCopy = allMaterialsCopy?.find((material) =>
          compareStrings(material.pathName, currentMaterialPath)
        );
        if (
          allMaterialsCopy &&
          allMaterialsCopy.data &&
          allMaterialsCopy.data.length > 0
        ) {
          materialsOptions.push(...allMaterialsCopy?.data);
        }
      }
    }

    if (!isSwitch) {
      if (
        (isMaterial && materialsOptions?.length === 0) ||
        (!isMaterial &&
          (!parameter?.valuesConfigs || parameter?.valuesConfigs.length === 0))
      ) {
        //alertFault("alerts.noValues");
        ///return;
      }
    }

    //Extract externalCode for the materials/switch that come from productParametersExternalValues
    if (isMaterial || isSwitch || isInput) {
      let valuesConfigs = [];
      if (parameter?.productParametersExternalValues) {
        const externalValuesMap = new Map(
          parameter.productParametersExternalValues.map((ev) => [
            ev.valueId,
            ev.externalCode,
          ])
        );

        if (isMaterial) {
          valuesConfigs = materialsOptions.map((materialOption) => ({
            ...materialOption,
            externalValue:
              externalValuesMap.get(materialOption.valueId) || null,
          }));
        } else if (isSwitch) {
          valuesConfigs = [
            {
              value: t("products.parameters.true"),
              valueId: "true",
              externalValue: externalValuesMap.get("true") || null,
            },
            {
              value: t("products.parameters.false"),
              valueId: "false",
              externalValue: externalValuesMap.get("false") || null,
            },
          ];
        } else {
          valuesConfigs = parameter?.productParametersExternalValues.map(
            (x) => ({
              updateName: x.valueId,
              externalValue: x.externalCode,
            })
          );
        }
      }
      // Set the parameter with updated valuesConfigs
      const updatedParameter = {
        ...parameter,
        valuesConfigs,
      };
      setSelectedParameter(updatedParameter);
    } else {
      setSelectedParameter(parameter);
    }
    setSelectedSectonId(sectionId);
    setSelectedSubSection(subSectionId);

    setTimeout(() => {
      setOpenExternalIdsModal(true);
    }, 100);
  };

  const onCloseExternalIdsModal = () => {
    setSelectedParameter({});
    setOpenExternalIdsModal(false);
  };

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isUpdatingParameter, setIsUpdatingParameter] = useState(false);
  const [selectedOption, setSelectedOption] = useState<any>({});
  const [selectedSectionId, setSelectedSectionId] = useState<any>({});
  const [selectedSubSectionId, setSelectedSubSectionId] = useState<any>({});
  const [selectedUpdateType, setSelectedUpdateType] = useRecoilState(
    selectedUpdateTypeState
  );
  const [doNotAskNextTime, setDoNotAskNextTime] = useRecoilState(
    doNotAskNextTimeState
  );

  const handleCloseModal = () => {
    setIsUpdateModalOpen(false);
    // setSelectedUpdateType(null);
    setSelectedOption(null);
    setSelectedSectionId(null);
    setSelectedSubSectionId(null);
  };

  const handleOpenModal = (action: "hidden" | "ai" = "hidden") => {
    valueConfigActionRef.current = action;
    if (isVariationProductMode || router.asPath?.includes("/settings/templates/")) {
      return;
    }
    if (!doNotAskNextTime) {
      setIsUpdateModalOpen(true);
    }
  };

  const openVisibilityDraftSession = useCallback(
    (
      sectionId: string,
      subSectionId: string,
      parameter: any,
      isMaterial: boolean
    ) => {
      const nextUpdateType = isVariationProductMode
        ? UpdateTypes.PRODUCT_ONLY
        : selectedUpdateType === null
          ? undefined
          : selectedUpdateType;
      const freshValuesConfigs = cloneValuesConfigsForParameter(
        templateRef.current,
        parameter
      );
      setVisibilityDraft((current) => {
        if (current != null && current.parameterId === parameter.id) {
          openVisibilityReusedSessionRef.current = true;
          return {
            ...current,
            sectionId,
            subSectionId,
            isMaterial,
            updateType: current.updateType ?? nextUpdateType,
            valuesConfigs: visibilityDraftDirtyRef.current
              ? current.valuesConfigs
              : freshValuesConfigs,
          };
        }
        openVisibilityReusedSessionRef.current = false;
        return {
          parameterId: parameter.id,
          sectionId,
          subSectionId,
          valuesConfigs: freshValuesConfigs,
          isMaterial,
          updateType: nextUpdateType,
        };
      });
      if (!openVisibilityReusedSessionRef.current) {
        setVisibilityDraftDirty(false);
      }
    },
    [selectedUpdateType, isVariationProductMode]
  );

  const saveVisibilityDraft = useCallback(async () => {
    if (!visibilityDraft || !visibilityDraftDirty) {
      return;
    }
    const updateTypeToUse =
      visibilityDraft.updateType ??
      (isVariationProductMode || router.asPath?.includes("/settings/templates/") ? UpdateTypes.PRODUCT_ONLY : selectedUpdateType) ??
      undefined;
    if (updateTypeToUse === null || updateTypeToUse === undefined) {
      alertFault(t("products.parameters.pleaseSelectOneOption"));
      return;
    }

    setVisibilitySaveLoading(true);
    try {
      const root = templateRef.current;
      const parameterId = visibilityDraft.parameterId;
      const base =
        (root && findParameterByIdInTemplate(root, parameterId)) || null;
      if (!base) {
        return;
      }

      const data = {
        ...base,
        valuesConfigs: visibilityDraft.valuesConfigs,
      };

      await updatedValuesConfigsForParameters(
        visibilityDraft.sectionId,
        visibilityDraft.subSectionId,
        data,
        undefined,
        EUpdateActionType.hiddenValue,
        updateTypeToUse,
        ProductLogActionEnum.UpdateParameterDefaultValue
      );

      setVisibilityDraft(null);
      setVisibilityDraftDirty(false);
      if (visibilityPaperContextRef.current === parameterId) {
        visibilityPaperContextRef.current = null;
      }
    } finally {
      setVisibilitySaveLoading(false);
    }
  }, [
    visibilityDraft,
    visibilityDraftDirty,
    selectedUpdateType,
    isVariationProductMode,
    updatedValuesConfigsForParameters,
    alertFault,
    t,
  ]);

  const ParameterVisibilityDropdownPaper = useMemo(() => {
    return forwardRef<HTMLDivElement, Record<string, unknown>>(
      function ParameterVisibilityDropdownPaperInner(props, ref) {
        const { children, ...rest } = props as {
          children?: ReactNode;
        } & Record<string, unknown>;
        const pid = visibilityPaperContextRef.current;
        const showFooter =
          !!visibilityDraft &&
          visibilityDraftDirty &&
          visibilityDraft.parameterId === pid;

        return (
          <Paper ref={ref} {...(rest as object)}>
            {children as ReactNode}
            {showFooter && (
              <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                sx={{
                  p: 1,
                  borderTop: 1,
                  borderColor: "divider",
                }}
              >
                <GomakePrimaryButton
                  disabled={visibilitySaveLoading}
                  style={{
                    width: "auto",
                    minWidth: 72,
                    height: 34,
                    padding: "5px 14px",
                    fontSize: 14,
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    void saveVisibilityDraft();
                  }}
                >
                  {t("materials.buttons.save")}
                </GomakePrimaryButton>
              </Stack>
            )}
          </Paper>
        );
      }
    );
  }, [
    visibilityDraft,
    visibilityDraftDirty,
    visibilitySaveLoading,
    saveVisibilityDraft,
    t,
  ]);

  const updatedProductParameterValuesConfigsHidden = useCallback(
    async (
      sectionId: string,
      subSectionId: string,
      parameter: any,
      option: any,
      updateType?: UpdateTypes
    ) => {
      toggleStandardVisibilityInDraft(
        sectionId,
        subSectionId,
        parameter,
        option,
        updateType
      );
    },
    [toggleStandardVisibilityInDraft]
  );

  const updatedParameterMaterialTypeValuesConfigsHidden = useCallback(
    async (
      sectionId: string,
      subSectionId: string,
      parameter: any,
      option: any,
      updateType?: UpdateTypes
    ) => {
      toggleMaterialVisibilityInDraft(
        sectionId,
        subSectionId,
        parameter,
        option,
        updateType
      );
    },
    [toggleMaterialVisibilityInDraft]
  );

  const [isMaterialParameter, setIsMaterialParameter] =
    useState<boolean>(false);
  const handleUpdateParametersValue = async (
    isMaterial: boolean,
    sectionId: string,
    subSectionId: string,
    parameter: any,
    option: any,
    updateType?: UpdateTypes,
    persistImmediately = false
  ) => {
    const resolvedUpdateType =
      updateType !== null && updateType !== undefined
        ? updateType
        : router.asPath?.includes("/settings/templates/")
          ? UpdateTypes.PRODUCT_ONLY
          : selectedUpdateType;
    if (resolvedUpdateType === null || resolvedUpdateType === undefined) {
      alertFault(t("products.parameters.pleaseSelectOneOption"));
      return;
    }
    if (persistImmediately) {
      setIsUpdatingParameter(true);
      try {
        await persistVisibilityToggleNow(
          isMaterial,
          sectionId,
          subSectionId,
          parameter,
          option,
          resolvedUpdateType
        );
      } finally {
        setIsUpdatingParameter(false);
      }
    } else if (isMaterial) {
      toggleMaterialVisibilityInDraft(
        sectionId,
        subSectionId,
        parameter,
        option,
        resolvedUpdateType
      );
    } else {
      toggleStandardVisibilityInDraft(
        sectionId,
        subSectionId,
        parameter,
        option,
        resolvedUpdateType
      );
    }

    handleCloseModal();
  };

  const confirmValueConfigModalUpdate = async () => {
    if (valueConfigActionRef.current === "ai") {
      const resolvedUpdateType =
        selectedUpdateType !== null && selectedUpdateType !== undefined
          ? selectedUpdateType
          : isVariationProductMode || router.asPath?.includes("/settings/templates/")
            ? UpdateTypes.PRODUCT_ONLY
            : selectedUpdateType;
      if (resolvedUpdateType === null || resolvedUpdateType === undefined) {
        alertFault(t("products.parameters.pleaseSelectOneOption"));
        return;
      }
      setIsUpdatingParameter(true);
      try {
        await persistAiSuggestionToggleNow(
          isMaterialParameter,
          selectedSectionId,
          selectedSubSectionId,
          selectedParameter,
          selectedOption,
          resolvedUpdateType
        );
      } finally {
        setIsUpdatingParameter(false);
      }
      handleCloseModal();
      return;
    }
    await handleUpdateParametersValue(
      isMaterialParameter,
      selectedSectionId,
      selectedSubSectionId,
      selectedParameter,
      selectedOption,
      selectedUpdateType,
      true
    );
  };

  const getEffectiveValuesConfigs = (parameter: any) => {
    if (
      visibilityDraft?.parameterId === parameter?.id &&
      visibilityDraftDirty
    ) {
      return visibilityDraft.valuesConfigs;
    }
    return parameter?.valuesConfigs;
  };

  const _renderParameterType = (
    sectionId,
    subSectionId,
    parameter,
    subSectionParameters,
    index,
    section,
    subSection
  ) => {
    const visibilityScopeUpdateType = isVariationProductMode || router.asPath?.includes("/settings/templates/")
      ? UpdateTypes.PRODUCT_ONLY
      : selectedUpdateType;
    const aiSuggestionIsMaterial =
      parameter?.parameterType === EParameterTypes.SELECT_MATERIALS ||
      parameter?.parameterType === EParameterTypes.ADVERTISING_PRODUCT_CATEGORY ||
      parameter?.parameterType === EParameterTypes.ADVERTISING_PRODUCT_NAME;
    const renderAiSuggestionIcon = (option: any) => (
      <Tooltip
        title={t(
          option?.isAiSuggestion
            ? "tooltips.aiSuggestion"
            : "tooltips.aiSuggestionOff"
        )}
      >
        <div
          style={{ cursor: "pointer", display: "flex" }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            captureVisibilityDropdownScroll(e);
            setIsMaterialParameter(aiSuggestionIsMaterial);
            setSelectedSectionId(sectionId);
            setSelectedSubSectionId(subSectionId);
            setSelectedParameter(parameter);
            setSelectedOption(option);
            if (!doNotAskNextTime && !isVariationProductMode && !router.asPath?.includes("/settings/templates/")) {
              handleOpenModal("ai");
            } else {
              void persistAiSuggestionToggleNow(
                aiSuggestionIsMaterial,
                sectionId,
                subSectionId,
                parameter,
                option,
                visibilityScopeUpdateType
              );
            }
          }}
        >
          <AIIcon
            width={18}
            height={18}
            color={
              option?.isAiSuggestion
                ? "var(--theme-accent-color, #ED028C)"
                : "#A4A4A4"
            }
          />
        </div>
      </Tooltip>
    );

    if (parameter?.parameterType === EParameterTypes.INPUT_NUMBER) {
      return (
        <GomakeTextInput
          style={classes.textInputStyle}
          defaultValue={parameter.defaultValue}
          placeholder={parameter.name}
          onChange={(e: any) => setChangeDefaultValue(e.target.value)}
          onBlur={() =>
            updatedProductParameteDefaultValue(
              sectionId,
              subSectionId,
              parameter
            )
          }
          type="number"
          disabled={parameter?.isHidden}
        />
      );
    } else if (parameter?.parameterType === EParameterTypes.INPUT_TEXT) {
      return (
        <GomakeTextInput
          style={classes.textInputStyle}
          defaultValue={parameter.defaultValue}
          placeholder={parameter.name}
          type="text"
          onChange={(e: any) => setChangeDefaultValue(e.target.value)}
          onBlur={() =>
            updatedProductParameteDefaultValue(
              sectionId,
              subSectionId,
              parameter
            )
          }
          disabled={parameter?.isHidden}
        />
      );
    } else if (
      parameter?.parameterType === EParameterTypes.DROP_DOWN_LIST ||
      parameter?.parameterType === EParameterTypes.MACHINES_SELECT
    ) {
      const effectiveValuesConfigs = getEffectiveValuesConfigs(parameter);
      const defaultObject = effectiveValuesConfigs?.find(
        (item) => item.isDefault === true
      );
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            gap: "8px",
          }}
        >
          <GoMakeAutoComplate
            key={`${parameter?.id}-${defaultObject?.id}-${defaultObject?.updateName ?? ""}`}
            options={effectiveValuesConfigs || []}
            disabled={parameter?.isHidden}
            placeholder={parameter.name}
            style={classes.dropDownListStyle}
            getOptionLabel={(option: any) => option.updateName}
            defaultValue={defaultObject}
            PaperComponent={ParameterVisibilityDropdownPaper}
            componentsProps={visibilityParameterAutocompleteComponentsProps}
            onOpen={() => {
              visibilityPaperContextRef.current = parameter.id;
              openVisibilityDraftSession(
                sectionId,
                subSectionId,
                parameter,
                false
              );
            }}
            onClose={() => {
              if (visibilityPaperContextRef.current === parameter.id) {
                visibilityPaperContextRef.current = null;
                if (!visibilityDraftDirtyRef.current) {
                  setVisibilityDraft(null);
                }
              }
            }}
            onChange={(e: any, value: any) => {
              updatedProductParameterValuesConfigsDefault(
                sectionId,
                subSectionId,
                parameter,
                value
              );
            }}
            renderOption={(props: any, option: any) => {
              return (
                <div style={classes.optionsContainer}>
                  <div
                    {...props}
                    style={{ width: "100%", alignSelf: "center", marginTop: 5 }}
                  >
                    {option.updateName}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {renderAiSuggestionIcon(option)}
                    {option.isHidden === true ? (
                      <div
                        style={{ cursor: "pointer" }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={(e) => {
                          captureVisibilityDropdownScroll(e);
                          setIsMaterialParameter(false);
                          setSelectedSectionId(sectionId);
                          setSelectedSubSectionId(subSectionId);
                          setSelectedParameter(parameter);
                          setSelectedOption(option);
                          if (!doNotAskNextTime && !isVariationProductMode && !router.asPath?.includes("/settings/templates/")) {
                            handleOpenModal();
                          } else {
                            handleUpdateParametersValue(
                              false,
                              sectionId,
                              subSectionId,
                              parameter,
                              option,
                              visibilityScopeUpdateType
                            );
                          }
                        }}
                      >
                        <HiddenIcon />
                      </div>
                    ) : (
                      <div
                        style={{ cursor: "pointer" }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={(e) => {
                          captureVisibilityDropdownScroll(e);
                          setIsMaterialParameter(false);
                          setSelectedSectionId(sectionId);
                          setSelectedSubSectionId(subSectionId);
                          setSelectedParameter(parameter);
                          setSelectedOption(option);
                          if (!doNotAskNextTime && !isVariationProductMode && !router.asPath?.includes("/settings/templates/")) {
                            handleOpenModal();
                          } else {
                            handleUpdateParametersValue(
                              false,
                              sectionId,
                              subSectionId,
                              parameter,
                              option,
                              visibilityScopeUpdateType
                            );
                          }
                        }}
                      >
                        <NotHiddenIcon />
                      </div>
                    )}
                  </div>
                </div>
              );
            }}
          />
          {parameter?.setSettingIcon && !router.asPath?.includes("/settings/templates/") && (
            <div
              style={{ cursor: "pointer" }}
              onClick={() => {
                onOpeneMultiParameterModal(
                  parameter,
                  subSection,
                  section,
                  subSectionParameters
                );
              }}
            >
              <SettingsIcon
                stroke={"var(--theme-secondary-color, #ED028C)"}
                width={24}
                height={24}
              />
            </div>
          )}
        </div>
      );
    } else if (parameter?.parameterType === EParameterTypes.SWITCH) {
      return (
        <SecondSwitch
          disabled={parameter?.isHidden}
          checked={parameter?.defaultValue === "true"}
          onChange={(a: any, value: any) => {
            updatedProductParameteDefaultValueForSwitch(
              sectionId,
              subSectionId,
              parameter,
              value
            );
          }}
        />
      );
    } else if (
      parameter?.parameterType === EParameterTypes.SELECT_CHILDS_PARAMETERS
    ) {
      const effectiveValuesConfigsChild = getEffectiveValuesConfigs(parameter);
      const defaultObject = effectiveValuesConfigsChild?.find(
        (item) => item.isDefault === true
      );
      return (
        <GoMakeAutoComplate
          key={`${parameter?.id}-${defaultObject?.id}-${defaultObject?.updateName ?? ""}`}
          options={effectiveValuesConfigsChild}
          disabled={parameter?.isHidden}
          placeholder={parameter.name}
          style={classes.dropDownListStyle}
          getOptionLabel={(option: any) => option.updateName}
          defaultValue={defaultObject}
          PaperComponent={ParameterVisibilityDropdownPaper}
          componentsProps={visibilityParameterAutocompleteComponentsProps}
          onOpen={() => {
            visibilityPaperContextRef.current = parameter.id;
            openVisibilityDraftSession(
              sectionId,
              subSectionId,
              parameter,
              false
            );
          }}
          onClose={() => {
            if (visibilityPaperContextRef.current === parameter.id) {
              visibilityPaperContextRef.current = null;
              if (!visibilityDraftDirtyRef.current) {
                setVisibilityDraft(null);
              }
            }
          }}
          onChange={(e: any, value: any) => {
            updatedProductParameterValuesConfigsDefault(
              sectionId,
              subSectionId,
              parameter,
              value
            );
          }}
          renderOption={(props: any, option: any) => {
            return (
              <div style={classes.optionsContainer}>
                <div {...props} style={{ width: "100%" }}>
                  {option.updateName}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {renderAiSuggestionIcon(option)}
                  {option.isHidden === true ? (
                    <div
                      style={{ cursor: "pointer" }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        captureVisibilityDropdownScroll(e);
                        setIsMaterialParameter(false);
                        setSelectedSectionId(sectionId);
                        setSelectedSubSectionId(subSectionId);
                        setSelectedParameter(parameter);
                        setSelectedOption(option);
                        if (!doNotAskNextTime && !isVariationProductMode && !router.asPath?.includes("/settings/templates/")) {
                          handleOpenModal();
                        } else {
                          handleUpdateParametersValue(
                            false,
                            sectionId,
                            subSectionId,
                            parameter,
                            option,
                            visibilityScopeUpdateType
                          );
                        }
                      }}
                    >
                      <HiddenIcon />
                    </div>
                  ) : (
                    <div
                      style={{ cursor: "pointer" }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        captureVisibilityDropdownScroll(e);
                        setIsMaterialParameter(false);
                        setSelectedSectionId(sectionId);
                        setSelectedSubSectionId(subSectionId);
                        setSelectedParameter(parameter);
                        setSelectedOption(option);
                        if (!doNotAskNextTime && !isVariationProductMode && !router.asPath?.includes("/settings/templates/")) {
                          handleOpenModal();
                        } else {
                          handleUpdateParametersValue(
                            false,
                            sectionId,
                            subSectionId,
                            parameter,
                            option,
                            visibilityScopeUpdateType
                          );
                        }
                      }}
                    >
                      <NotHiddenIcon />
                    </div>
                  )}
                </div>
              </div>
            );
          }}
        />
      );
    } else if (
      parameter?.parameterType === EParameterTypes.SELECT_MATERIALS ||
      parameter?.parameterType ===
      EParameterTypes.ADVERTISING_PRODUCT_CATEGORY ||
      parameter?.parameterType === EParameterTypes.ADVERTISING_PRODUCT_NAME
    ) {
      const effectiveMaterialValuesConfigs =
        getEffectiveValuesConfigs(parameter);
      const defailtObjectValue = effectiveMaterialValuesConfigs?.find(
        (item) => item.isDefault === true
      );
      let options = [];
      let selectedOption;
      const materialPath = parameter?.materialPath;
      if (
        allMaterials?.length > 0 &&
        Array.isArray(materialPath) &&
        materialPath.length > 0
      ) {
        const currentMaterialPath = materialPath[materialPath.length - 1];
        const parentMaterialPath = materialPath.slice(0, -1);
        let allMaterialsCopy = cloneDeep(allMaterials);
        if (parentMaterialPath && parentMaterialPath.length > 0) {
          allMaterialsCopy = allMaterialsCopy?.find((material) =>
            compareStrings(material.pathName, parentMaterialPath[0])
          );
          for (let i = 0; i < parentMaterialPath.length; i++) {
            const currentPath = parentMaterialPath.slice(0, i + 1).toString();
            const parentParameter = subSectionParameters.find(
              (x) => x.materialPath && x.materialPath.toString() == currentPath
            );
            if (parentParameter) {
              /*allMaterialsCopy = allMaterialsCopy?.data?.find(material =>
                  compareStrings(material.pathName, parentMaterialPath[i])
              )?.data;*/
              const parentParameterDefaultValueConfig =
                parentParameter?.valuesConfigs?.find(
                  (item) => item.isDefault === true
                );
              if (parentParameterDefaultValueConfig) {
                const parentParameterValue =
                  parentParameterDefaultValueConfig.materialValueIds[0].valueId;
                allMaterialsCopy = allMaterialsCopy?.data.find(
                  (x) => x.valueId === parentParameterValue
                );
              } else {
                allMaterialsCopy = {
                  data: [],
                };
              }
            }
          }
          if (allMaterialsCopy) {
            options = [];
            if (allMaterialsCopy && allMaterialsCopy.data) {
              options.push(...allMaterialsCopy.data);
            }
          }
        } else {
          allMaterialsCopy = allMaterialsCopy?.find((material) =>
            compareStrings(material.pathName, currentMaterialPath)
          );
          if (
            allMaterialsCopy &&
            allMaterialsCopy.data &&
            allMaterialsCopy.data.length > 0
          ) {
            //debugger
            /*if(isVariationProductMode && allMaterialsCopy?.data && allMaterialsCopy.data.length > 0){
              allMaterialsCopy?.data.forEach((item) => item.isHidden = true)
            }*/
            options.push(...allMaterialsCopy?.data);
          }
        }
        materialListOptionsByParameterIdRef.current.set(parameter.id, options);
        if (defailtObjectValue?.materialValueIds?.[0]?.valueId) {
          selectedOption = options.find(
            (option) =>
              option.valueId ===
              defailtObjectValue.materialValueIds[0].valueId
          );
        }
      }
      return (
          <GoMakeAutoComplate
            options={options?.length > 0 ? options : []}
            disabled={parameter?.isHidden}
            placeholder={parameter.name}
            style={classes.dropDownListStyle}
            defaultValue={selectedOption}
            getOptionLabel={(option: any) => option.value}
            PaperComponent={ParameterVisibilityDropdownPaper}
            componentsProps={visibilityParameterAutocompleteComponentsProps}
            onOpen={() => {
              visibilityPaperContextRef.current = parameter.id;
              openVisibilityDraftSession(
                sectionId,
                subSectionId,
                parameter,
                true
              );
            }}
            onClose={() => {
              if (visibilityPaperContextRef.current === parameter.id) {
                visibilityPaperContextRef.current = null;
                if (!visibilityDraftDirtyRef.current) {
                  setVisibilityDraft(null);
                }
              }
            }}
            onChange={(e: any, value: any) => {
              updatedParameterMaterialTypeValuesConfigsDefault(
                sectionId,
                subSectionId,
                parameter,
                value
              );
            }}
            renderOption={(props: any, option: any) => {
              function checkValueIdAndHidden(valueId) {
                const matchedConfig = effectiveMaterialValuesConfigs?.find(
                  (config) =>
                    config.materialValueIds.some((id) => id.valueId === valueId)
                );
                if (isVariationProductMode && !matchedConfig) {
                  return true;
                }
                return matchedConfig && matchedConfig.isHidden === true;
              }

              return (
                <div style={classes.optionsContainer}>
                  <div {...props} style={{ width: "100%" }}>
                    {option?.value}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {renderAiSuggestionIcon(option)}
                    {checkValueIdAndHidden(option?.valueId) ? (
                      <div
                        style={{ cursor: "pointer" }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={(e) => {
                          captureVisibilityDropdownScroll(e);
                          setIsMaterialParameter(true);
                          setSelectedSectionId(sectionId);
                          setSelectedSubSectionId(subSectionId);
                          setSelectedParameter(parameter);
                          setSelectedOption(option);
                          if (!doNotAskNextTime && !isVariationProductMode && !router.asPath?.includes("/settings/templates/")) {
                            handleOpenModal();
                          } else {
                            handleUpdateParametersValue(
                              true,
                              sectionId,
                              subSectionId,
                              parameter,
                              option,
                              visibilityScopeUpdateType
                            );
                          }
                        }}
                      >
                        <HiddenIcon />
                      </div>
                    ) : (
                      <div
                        style={{ cursor: "pointer" }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={(e) => {
                          captureVisibilityDropdownScroll(e);
                          setIsMaterialParameter(true);
                          setSelectedSectionId(sectionId);
                          setSelectedSubSectionId(subSectionId);
                          setSelectedParameter(parameter);
                          setSelectedOption(option);
                          if (!doNotAskNextTime && !isVariationProductMode && !router.asPath?.includes("/settings/templates/")) {
                            handleOpenModal();
                          } else {
                            handleUpdateParametersValue(
                              true,
                              sectionId,
                              subSectionId,
                              parameter,
                              option,
                              visibilityScopeUpdateType
                            );
                          }
                        }}
                      >
                        <NotHiddenIcon />
                      </div>
                    )}
                  </div>
                </div>
              );
            }}
          />
        );
    } else if (
      parameter?.parameterType ===
      EParameterTypes.SELECT_FIXED_VALUES_WITH_IMAGES
    ) {
      const effectiveFixedImageConfigs = getEffectiveValuesConfigs(parameter);
      const defaultObject = effectiveFixedImageConfigs?.find(
        (item) => item.isDefault === true
      );
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            gap: "8px",
          }}
        >
          <GoMakeAutoComplate
            key={`${parameter?.id}-${defaultObject?.id}-${defaultObject?.updateName ?? ""}`}
            options={effectiveFixedImageConfigs}
            disabled={parameter?.isHidden}
            placeholder={parameter.name}
            style={classes.dropDownListStyle}
            getOptionLabel={(option: any) => option.updateName}
            defaultValue={defaultObject}
            PaperComponent={ParameterVisibilityDropdownPaper}
            componentsProps={visibilityParameterAutocompleteComponentsProps}
            onOpen={() => {
              visibilityPaperContextRef.current = parameter.id;
              openVisibilityDraftSession(
                sectionId,
                subSectionId,
                parameter,
                false
              );
            }}
            onClose={() => {
              if (visibilityPaperContextRef.current === parameter.id) {
                visibilityPaperContextRef.current = null;
                if (!visibilityDraftDirtyRef.current) {
                  setVisibilityDraft(null);
                }
              }
            }}
            onChange={(e: any, value: any) => {
              updatedProductParameterValuesConfigsDefault(
                sectionId,
                subSectionId,
                parameter,
                value
              );
            }}
            renderOption={(props: any, option: any) => {
              return (
                <div style={classes.optionsContainer}>
                  <div
                    {...props}
                    style={{ width: "100%", alignSelf: "center", marginTop: 5 }}
                  >
                    {option?.imageUrl && (
                      <img
                        src={option?.imageUrl}
                        alt={option.updateName}
                        style={{
                          width: "40px",
                          height: "40px",
                          margin: "10px",
                          objectFit: "cover",
                        }}
                      />
                    )}
                    {option.updateName}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {renderAiSuggestionIcon(option)}
                    {option.isHidden ? (
                      <div
                        style={{ cursor: "pointer" }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={(e) => {
                          captureVisibilityDropdownScroll(e);
                          setIsMaterialParameter(false);
                          setSelectedSectionId(sectionId);
                          setSelectedSubSectionId(subSectionId);
                          setSelectedParameter(parameter);
                          setSelectedOption(option);
                          if (!doNotAskNextTime && !isVariationProductMode && !router.asPath?.includes("/settings/templates/")) {
                            handleOpenModal();
                          } else {
                            handleUpdateParametersValue(
                              false,
                              sectionId,
                              subSectionId,
                              parameter,
                              option,
                              visibilityScopeUpdateType
                            );
                          }
                        }}
                      >
                        <HiddenIcon />
                      </div>
                    ) : (
                      <div
                        style={{ cursor: "pointer" }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={(e) => {
                          captureVisibilityDropdownScroll(e);
                          setIsMaterialParameter(false);
                          setSelectedSectionId(sectionId);
                          setSelectedSubSectionId(subSectionId);
                          setSelectedParameter(parameter);
                          setSelectedOption(option);
                          if (!doNotAskNextTime && !isVariationProductMode && !router.asPath?.includes("/settings/templates/")) {
                            handleOpenModal();
                          } else {
                            handleUpdateParametersValue(
                              false,
                              sectionId,
                              subSectionId,
                              parameter,
                              option,
                              visibilityScopeUpdateType
                            );
                          }
                        }}
                      >
                        <NotHiddenIcon />
                      </div>
                    )}
                  </div>
                </div>
              );
            }}
          />
          {parameter?.setSettingIcon && !router.asPath?.includes("/settings/templates/") && (
            <div
              style={{ cursor: "pointer" }}
              onClick={() => {
                onOpeneMultiParameterModal(
                  parameter,
                  subSection,
                  section,
                  subSectionParameters
                );
              }}
            >
              <SettingsIcon
                stroke={"var(--theme-secondary-color, #ED028C)"}
                width={24}
                height={24}
              />
            </div>
          )}
        </div>
      );
    } else if (parameter?.parameterType === EParameterTypes.BUTTON) {
      return (
        <ButtonParameterWidget
          classes={classes}
          parameter={parameter}
          selectBtnTypeToAction={selectBtnTypeToAction}
          subSection={subSection}
          section={section}
          index={index}
          straightKnife={straightKnife}
          isParameterSettings={true}
          allMaterials={allMaterials}
          template={template}
          getProductById={getProductById}
        />
      );
    }
  };

  const selectBtnTypeToAction = (
    parameter,
    sectionId,
    subSectionId,
    index,
    paameterType
  ) => {
    if (parameter?.buttonAction === EButtonTypes.GALLERY_MODAL) {
      setSelectParameterButton({
        parameter,
        sectionId,
        subSectionId,
        index,
        paameterType,
      });
      onOpeneGalleryModal();
    }
  };

  return {
    setOpenModal,
    setSelectedParameter,
    setSelectedSectonId,
    setSelectedSubSection,
    onCloseModal,
    _renderParameterType,
    onOpenModal,
    t,
    handleTabClick,
    handleNextClick,
    handleCalculateProductVariations,
    handleConfirmCalculateProductVariations,
    handleCloseVariationsSummaryModal,
    handlePreviousClick,
    onClickParametersTab,
    onChangeStateProduct,
    updatedProductParameterHidden,
    updatedProductParameterAiSuggestion: async (
      sectionId: string,
      subSectionId: string,
      parameter: any,
      value: string[],
      updateType: UpdateTypes
    ) => {
      await updateProductParameterEndPoint(sectionId, subSectionId, {
        parameter: {
          ...parameter,
          aiSuggestionValues: value,
        },
        actionEnum: ProductLogActionEnum.UpdateParameterDefaultValue,
        updatedActionType: EUpdateActionType.aiSuggestionValue,
        updateType,
      });
    },
    confirmValueConfigModalUpdate,
    updatedProductParameterShowToClient,
    saveTemplateParameterSettings,
    updatedProductParameterTranslations,
    updatedProductParameteRequierd,
    updatedProductParameteLocked,
    updatedProductParameteName,
    setChangeName,
    setChangeDefaultValue,
    updatedProductParameteDefaultValue,
    updatedProductParameteDefaultValueForSwitch,
    updatedProductParameterValuesConfigsHidden,
    updatedProductParameterValuesConfigsDefault,
    updatedValuesConfigsForParameters,
    savePendingTemplateParameters,
    pendingTemplateParameterCount,
    savingTemplateParameters,
    setTemplate,
    getProductById,
    changeDefaultValue,
    changeName,
    productState,
    activeIndex,
    template,
    activeTab,
    tabs,
    selectedSubSection,
    selectedParameter,
    openModal,
    expanded,
    handleChange,
    relatedParameters,
    GalleryModalOpen,
    onCloseGalleryModal,
    onOpeneGalleryModal,
    straightKnife,
    multiParameterModal,
    onCloseMultiParameterModal,
    onOpeneMultiParameterModal,
    colorOptionsModalOpen,
    onCloseColorOptionsModal,
    onOpenColorOptionsModal,
    rollDirectionOptionsModalOpen,
    onCloseRollDirectionOptionsModal,
    onOpenRollDirectionOptionsModal,
    settingParameters,
    setSettingParameters,
    setSelectedUpdateType,
    selectedUpdateType,
    handleCloseModal,
    isUpdateModalOpen,
    isUpdatingParameter,
    handleUpdateParametersValue,
    setDoNotAskNextTime,
    doNotAskNextTime,
    isMaterialParameter,
    selectedSectonId,
    selectedOption,
    selectedSubSectionId,
    selectedSectionId,
    onOpenExternalIdsModal,
    onCloseExternalIdsModal,
    openExternalIdsModal,
    removeProductParamete,
    addSubSectionToProduct,
    addSectionToTemplate,
    updateSectionOnTemplate,
    updateSubSectionOnTemplate,
    deleteSectionFromTemplate,
    deleteSubSectionFromTemplate,
    queueAddExistingParameterToTemplate,
    addSubSectionToTemplate,
    isVariationProductMode,
    isCalculatingVariations,
    isConfirmingVariations,
    isVariationsSummaryModalOpen,
    productVariationsSummary,
  };
};

export { useAddProduct };
